/**
 * React Query Hooks for Requirements Management
 * Gap 5: Requirements Linkage & Traceability
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { API_BASE_URL } from '../api/config';
import { withActor } from '../utils/interlockActor';
import { throwIfNotOk } from '../utils/apiErrors';
import type {
  Requirement,
  RequirementFilter,
  CreateRequirementInput,
  UpdateRequirementInput,
  LinkRequirementInput,
  RequirementLink,
  VerificationRecordInput,
  RequirementVerificationRecord,
  RequirementChange,
  CoverageSummary,
  CoverageMatrix,
  TraceabilityChain,
  TraceabilityReportOptions,
  TraceabilityReport,
  RequirementImportResult,
} from '../types/requirements';

const API_BASE = `${API_BASE_URL}/requirements`;

// Query Keys
export const requirementKeys = {
  all: ['requirements'] as const,
  lists: () => [...requirementKeys.all, 'list'] as const,
  list: (filters: RequirementFilter) => [...requirementKeys.lists(), filters] as const,
  details: () => [...requirementKeys.all, 'detail'] as const,
  detail: (id: string) => [...requirementKeys.details(), id] as const,
  links: (id: string) => [...requirementKeys.detail(id), 'links'] as const,
  verification: (id: string) => [...requirementKeys.detail(id), 'verification'] as const,
  changes: (id: string) => [...requirementKeys.detail(id), 'changes'] as const,
  coverage: () => [...requirementKeys.all, 'coverage'] as const,
  matrix: (projectId: string) => [...requirementKeys.coverage(), 'matrix', projectId] as const,
  rtm: (projectId: string) => [...requirementKeys.all, 'rtm', projectId] as const,
  docRtm: (projectId: string) => [...requirementKeys.all, 'doc-rtm', projectId] as const,
  traceability: (id: string) => [...requirementKeys.all, 'traceability', id] as const,
};

// ---------------------------------------------------------------------------
// Doc-shaped RTM (302 008 003 layout — doc round-trip P4). The SAME canonical
// JSON the Word renderer consumes (GET /api/projects/:id/export/rtm.json), so
// the Traceability view on /interlock/requirements and the generated .docx can
// never disagree. Distinct from RtmResult above, which is the app-native
// verification-status dimension.
// ---------------------------------------------------------------------------

export interface RtmDocCitation {
  testId: string; // T-01 / A-01 / I-01 / S-01
  name: string;
}

export interface RtmDocRow {
  dbId: string; // Requirement DB id — drives the row → drill-down (?focus=)
  reqId: string; // SR-FN-001
  title: string; // the doc's "Requirement (short)" column
  design: string | null; // RequirementVerification notes (Design column)
  fat: RtmDocCitation[];
  sat: RtmDocCitation[];
  status: string; // "In design" / "Open (n TBC)" — derived from open DRs
  openDrCount: number;
  uncovered: boolean; // no FAT and no SAT citation — the coverage assertion
}

export interface RtmDocExport {
  projectId: string;
  projectName: string | null;
  generatedAt: string;
  rowCount: number;
  rows: RtmDocRow[];
  excluded: { reqId: string; title: string; reason: string }[];
  coverage: { uncovered: string[] };
}

// Requirements Traceability Matrix (verification dimension) types
export type RtmVerificationState =
  | 'NO_TEST'
  | 'HAS_TEST'
  | 'TEST_FAILED'
  | 'TEST_PASSED'
  | 'VERIFIED';

export interface RtmTestRef {
  linkId: string;
  testPlanId: string;
  testId: string;
  name: string;
  status: string;
  coverageStatus: string;
}

export interface RtmDesignRef {
  id: string;
  name: string;
  kind: 'COMPONENT' | 'FUNCTION';
}

/**
 * Owner-decision connective-tissue work — requirement -> FMEA coverage.
 * 'covered': reaches >=1 ANALYZED (scored) failure mode via a direct
 * RequirementFailureLink OR a RequirementFunction -> Function -> failure-mode
 * edge. 'linked-not-analyzed': reaches a failure mode but none are scored
 * yet. 'not-linked': reaches none via either edge. See requirementCoverage
 * .service.ts's FmeaCoverageState doc comment (backend) for the full
 * rationale — this mirrors it exactly.
 */
export type FmeaCoverageState = 'covered' | 'linked-not-analyzed' | 'not-linked';

export interface RtmRow {
  id: string;
  requirementId: string;
  title: string;
  description: string;
  type: string;
  priority: string;
  verificationMethod: string | null;
  verificationStatus: string;
  designRefs: RtmDesignRef[];
  tests: RtmTestRef[];
  derivedState: RtmVerificationState;
  isGap: boolean;
  fmeaCoverageState: FmeaCoverageState;
}

export interface RtmSummary {
  totalRequirements: number;
  verified: number;
  hasTestNotPassed: number;
  noTest: number;
  verificationCoveragePercentage: number;
  fmeaCovered: number;
  fmeaCoveragePercentage: number;
}

export interface RtmResult {
  projectId: string;
  rows: RtmRow[];
  summary: RtmSummary;
}

// Queries

// BLOCKER fix (real-data audit @ 642 requirements): the backend list route
// defaults to `limit=50` (packages/backend/src/routes/requirements.routes.ts)
// when no `limit` query param is supplied, and this hook never sent one — so
// every consumer (Requirements Manager, RTM feeds, the Status coverage tile,
// etc.) silently saw only the first 50 rows with no indication anything was
// cut off. The backend already computes and returns a `total` count
// alongside `data` (requirement.service.ts::getRequirements) ignoring
// limit/offset, and has no server-side cap on `limit` itself — so the
// correct, minimal fix is to explicitly request a limit comfortably above
// any real project's requirement count. Requirements Manager already
// full-scrolls its table (no virtualization), so loading the full set in one
// request is the right tradeoff at this scale; revisit with real pagination
// if a project ever approaches this ceiling.
const REQUIREMENTS_FETCH_LIMIT = 5000;

export const useRequirements = (filters?: RequirementFilter) => {
  return useQuery({
    queryKey: requirementKeys.list(filters || {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type.join(','));
      if (filters?.priority) params.append('priority', filters.priority.join(','));
      if (filters?.status) params.append('status', filters.status.join(','));
      if (filters?.verificationStatus)
        params.append('verificationStatus', filters.verificationStatus.join(','));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.tags) params.append('tags', filters.tags.join(','));
      if (filters?.projectId) params.append('projectId', filters.projectId);
      if (filters?.includeRetired) params.append('includeRetired', '1');
      params.append('limit', String(REQUIREMENTS_FETCH_LIMIT));

      const response = await fetch(`${API_BASE}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch requirements');
      const result = await response.json();
      // The backend's `total` (COUNT ignoring limit/offset — see
      // requirement.service.ts::getRequirements) is not surfaced separately
      // here: at REQUIREMENTS_FETCH_LIMIT the returned array already IS the
      // full set for every real project, so `data.length` === the true
      // total. Consumers that want an explicit count (e.g. Requirements
      // Manager's header) just read `.length`.
      return result.data as Requirement[];
    },
    enabled: !!filters?.projectId, // Only run query if projectId is present
    // Requirements-list resilience (follow-up from PR #212 review): keep the
    // previously-loaded page's data on screen while a refetch (e.g. a filter
    // change) is in flight or fails, instead of the list blanking to an
    // undefined/[] result. Callers distinguish "stale data still showing" via
    // `isPlaceholderData` if they need to.
    placeholderData: keepPreviousData,
  });
};

export const useRequirement = (id: string) => {
  return useQuery({
    queryKey: requirementKeys.detail(id),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/${id}`);
      if (!response.ok) throw new Error('Failed to fetch requirement');
      const result = await response.json();
      return result.data as Requirement;
    },
    enabled: !!id,
  });
};

export const useRequirementLinks = (id: string) => {
  return useQuery({
    queryKey: requirementKeys.links(id),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/${id}/links`);
      if (!response.ok) throw new Error('Failed to fetch requirement links');
      const result = await response.json();
      return result.data as RequirementLink[];
    },
    enabled: !!id,
  });
};

export const useRequirementVerificationRecords = (id: string) => {
  return useQuery({
    queryKey: requirementKeys.verification(id),
    queryFn: async () => {
      // Real backend route is plural (`/:requirementId/verifications`, added
      // for this hook — see requirements.routes.ts). It had no GET handler at
      // all before, so this hook could never have worked against a live API.
      // withActor carries the dev-switched identity (?actAs) — same rationale
      // as useUpdateRequirement/useAddVerificationRecord below — so a dev
      // impersonation session sees verification records attributed correctly.
      // includeRetired=1 so a RETIRED requirement's tombstoned evidence still
      // loads (L8 remedy). Safe for live requirements too: individual
      // verification deletes are HARD deletes, so a live requirement never has
      // tombstoned records — the result is identical without the flag.
      const response = await fetch(withActor(`${API_BASE}/${id}/verifications?includeRetired=1`));
      if (!response.ok) throw new Error('Failed to fetch verification records');
      const result = await response.json();
      return result.data as RequirementVerificationRecord[];
    },
    enabled: !!id,
  });
};

// GET /:requirementId/changes returns { Requirement, changes, totalChanges }
// (requirementChange.service.ts::getChangeHistory) — NOT a bare array — and
// each raw change row carries no top-level `riskLevel` or `impactDescription`
// (not columns on the Prisma model). `riskLevel` lives nested inside the
// `impactAnalysis` JSON blob (computed by analyzeImpact()); there is no free
// -text impact string at all, only affected-entity counts. This adapter maps
// the real backend shape onto the RequirementChangeHistory component's
// (finished, tested against fixtures) expected shape rather than changing
// that component — the mismatch is why this hook could never have worked
// against a live API before P3.
function adaptRequirementChange(id: string, raw: any): RequirementChange {
  const impact = raw.impactAnalysis as
    | {
        affectedComponents?: number;
        affectedFailures?: number;
        affectedVerifications?: number;
        riskLevel?: RequirementChange['riskLevel'];
      }
    | null
    | undefined;

  const affectedParts: string[] = [];
  if (impact?.affectedComponents) affectedParts.push(`${impact.affectedComponents} component(s)`);
  if (impact?.affectedFailures) affectedParts.push(`${impact.affectedFailures} failure mode(s)`);
  if (impact?.affectedVerifications)
    affectedParts.push(`${impact.affectedVerifications} verification(s)`);

  return {
    id: raw.id,
    requirementId: id,
    changeType: raw.changeType,
    fieldChanged: raw.fieldChanged ?? undefined,
    oldValue: raw.oldValue ?? undefined,
    newValue: raw.newValue ?? undefined,
    // No LOW fallback: a change whose impactAnalysis never computed a
    // riskLevel is UNKNOWN, not "confirmed low-risk" — RequirementChangeHistory's
    // own DEFAULT_RISK_CONFIG renders "Unknown" for an undefined riskLevel.
    riskLevel: impact?.riskLevel,
    impactDescription:
      raw.changeNotes ||
      (affectedParts.length ? `Affects ${affectedParts.join(', ')}.` : undefined),
    changedBy: raw.changedBy ?? undefined,
    changedAt: raw.changedAt,
    reviewStatus: raw.reviewStatus,
    reviewedBy: raw.reviewedBy ?? undefined,
    reviewedAt: raw.reviewedAt ?? undefined,
  };
}

export const useRequirementChanges = (id: string) => {
  return useQuery({
    queryKey: requirementKeys.changes(id),
    queryFn: async () => {
      // includeRetired=1 — W2-F, same "L8 remedy" idiom as
      // useRequirementVerifications above: a retired requirement's Change
      // History sub-panel previously 404'd (this endpoint's parent-existence
      // guard filtered deletedAt:null unconditionally), leaving a stuck
      // spinner on the retired detail panel. Live requirements are
      // unaffected — the flag only changes behaviour for a soft-deleted
      // parent.
      const response = await fetch(`${API_BASE}/${id}/changes?includeRetired=1`);
      if (!response.ok) throw new Error('Failed to fetch requirement changes');
      const result = await response.json();
      const rawChanges = result.data?.changes ?? [];
      return (rawChanges as any[]).map((raw) => adaptRequirementChange(id, raw));
    },
    enabled: !!id,
  });
};

export const useMarkChangeReviewed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // RequirementChangeHistory's catch already shows an error toast via
    // showMutationErrorToast, so suppress the global MutationCache toast to
    // avoid a duplicate toast (W8-A fix 2, same pattern as
    // useCreateRequirement/useUpdateRequirement above).
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({ changeId }: { changeId: string; requirementId: string }) => {
      // SECURITY (P3): reviewer identity comes from withActor's resolved
      // actor server-side (see requirements.routes.ts's fix); the body is
      // no longer read for `reviewedBy` at all, so nothing is sent here.
      const response = await fetch(
        withActor(`${API_BASE}/requirement-changes/${changeId}/mark-reviewed`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      await throwIfNotOk(response, 'Failed to mark change as reviewed');
      const result = await response.json();
      return result as { data: RequirementChange; selfReview: boolean };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.changes(variables.requirementId) });
    },
  });
};

export const useCoverageSummary = (projectId: string) => {
  return useQuery({
    queryKey: [...requirementKeys.coverage(), projectId],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/coverage/summary?projectId=${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch coverage summary');
      const result = await response.json();
      return result.data as CoverageSummary;
    },
    enabled: !!projectId,
  });
};

export const useCoverageMatrix = (projectId: string) => {
  return useQuery({
    queryKey: requirementKeys.matrix(projectId),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/coverage/matrix?projectId=${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch coverage matrix');
      const result = await response.json();
      return result.data as CoverageMatrix;
    },
    enabled: !!projectId,
  });
};

/**
 * Requirements Traceability Matrix (RTM) — verification-status view.
 * One row per requirement: verify method, linked design, verifying tests,
 * and a derived overall verification state.
 */
export const useRtm = (projectId: string) => {
  return useQuery({
    queryKey: requirementKeys.rtm(projectId),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/rtm?projectId=${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch RTM');
      const result = await response.json();
      return result.data as RtmResult;
    },
    enabled: !!projectId,
  });
};

/**
 * Doc-shaped RTM (302 008 003 Rev 3 layout) — doc round-trip P4.
 * Feeds the Traceability view on /interlock/requirements from the same
 * canonical serializer the Word export renders, engineering scope +
 * excluded-row report + computed coverage assertion included.
 */
export const useDocRtm = (projectId: string) => {
  return useQuery({
    queryKey: requirementKeys.docRtm(projectId),
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE_URL}/projects/${encodeURIComponent(projectId)}/export/rtm.json`
      );
      if (!response.ok) throw new Error('Failed to fetch doc RTM');
      return (await response.json()) as RtmDocExport;
    },
    enabled: !!projectId,
  });
};

export const useTraceabilityChain = (id: string) => {
  return useQuery({
    queryKey: requirementKeys.traceability(id),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/traceability/${id}`);
      if (!response.ok) throw new Error('Failed to fetch traceability chain');
      const result = await response.json();
      return result.data as TraceabilityChain;
    },
    enabled: !!id,
  });
};

// Mutations

export const useCreateRequirement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // CreateRequirementDialog's catch already shows a (403-aware) error toast
    // via showMutationErrorToast, so suppress the global MutationCache toast to
    // avoid a duplicate toast on a single failed create.
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateRequirementInput;
    }) => {
      const response = await fetch(withActor(`${API_BASE}?projectId=${projectId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(response, 'Failed to create requirement');
      const result = await response.json();
      return result.data as Requirement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.lists() });
    },
  });
};

export const useUpdateRequirement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Call sites (e.g. EditRequirementDialog) already show a (403/stale-aware)
    // error toast via showMutationErrorToast in their catch block, so suppress
    // the global MutationCache toast to avoid a duplicate toast on a single
    // failed update — same fix as useCreateRequirement above.
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({ id, data }: { id: string; data: UpdateRequirementInput }) => {
      // Backend exposes PUT /api/requirements/:id (no PATCH route) — use PUT so
      // in-app edits actually persist. withActor carries the dev-switched
      // identity (?actAs) so backend's resolveActor(req) (which this route
      // already calls) attributes the edit to the switched engineer instead
      // of always falling back to Local Admin (Fix 2, persona-audit).
      const response = await fetch(withActor(`${API_BASE}/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(response, 'Failed to update requirement');
      const result = await response.json();
      return result.data as Requirement;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: requirementKeys.lists() });
    },
  });
};

export const useDeleteRequirement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // RequirementsManager's confirmDelete catch already shows an error toast
    // via showMutationErrorToast, so suppress the global MutationCache toast
    // to avoid a duplicate toast (W8-A fix 2, same pattern as
    // useCreateRequirement/useUpdateRequirement above).
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (id: string) => {
      const response = await fetch(withActor(`${API_BASE}/${id}`), {
        method: 'DELETE',
      });
      await throwIfNotOk(response, 'Failed to delete requirement');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.lists() });
    },
  });
};

export const useLinkRequirement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LinkRequirementInput) => {
      const response = await fetch(`${API_BASE}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(response, 'Failed to link requirement');
      const result = await response.json();
      return result.data as RequirementLink;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.links(variables.requirementId) });
      queryClient.invalidateQueries({ queryKey: requirementKeys.coverage() });
    },
  });
};

export const useUnlinkRequirement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkId: string) => {
      const response = await fetch(`${API_BASE}/unlink/${linkId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to unlink requirement');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.all });
    },
  });
};

export const useAddVerificationRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VerificationRecordInput) => {
      // Real backend route is `POST /:requirementId/verifications` — the
      // requirement id belongs in the path, not just the body. withActor
      // carries the dev-switched identity (?actAs) so the backend's
      // resolveActor(req) attributes the verification sign-off to the
      // switched-in engineer instead of always falling back to Local Admin
      // — same fix as useUpdateRequirement above.
      const response = await fetch(withActor(`${API_BASE}/${data.requirementId}/verifications`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        // Surface the backend's specific validation message (e.g. "Cannot
        // record a passing verification result without evidence …") instead
        // of a generic failure string — the guard is correct; only the
        // messaging was being lost.
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || body?.message || 'Failed to add verification record');
      }
      const result = await response.json();
      return result.data as RequirementVerificationRecord;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: requirementKeys.verification(variables.requirementId),
      });
      queryClient.invalidateQueries({
        queryKey: requirementKeys.detail(variables.requirementId),
      });
      // Bug 1 fix (stalefix): the requirement detail view's Verification
      // badge (RequirementsPage.tsx) reads `verificationStatus` off the
      // REQUIREMENTS LIST query (`useRequirements`), not off a per-id detail
      // query — `selectedRequirement` is resolved via
      // `requirements.find(r => r.id === selectedRequirementId)`. The backend
      // does roll the requirement's `verificationStatus` up when a record is
      // added (requirementVerification.service.ts), but without invalidating
      // the list here too the badge kept showing the pre-mutation status
      // until a full reload — same pattern useUpdateRequirement already
      // follows below.
      queryClient.invalidateQueries({ queryKey: requirementKeys.lists() });
    },
  });
};

// D2 — RequirementVerification records had no edit path anywhere in the app
// (VerificationRecordPanel only had create/read); this is the client-side
// half of `PUT /api/verifications/:id` (requirements.routes.ts ~line 1191).
// Mirrors api/notes.ts's `updateNote`: a plain async function returning the
// updated record, throwing a status-carrying error (throwIfNotOk → ApiError)
// so callers can distinguish a 409 VERSION_CONFLICT from any other failure.
// `verifiedBy` is intentionally excluded from the input type — the backend
// strips it from the body anyway (SEC, L3 authz sweep), so it's not honest to
// send it.
export type UpdateVerificationRecordInput = Omit<VerificationRecordInput, 'requirementId'>;

export async function updateVerification(
  id: string,
  patch: UpdateVerificationRecordInput
): Promise<RequirementVerificationRecord> {
  const response = await fetch(withActor(`${API_BASE}/verifications/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  await throwIfNotOk(response, 'Failed to update verification record');
  const result = await response.json();
  return result.data as RequirementVerificationRecord;
}

export const useUpdateVerificationRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      // Not read here — mutationFn only needs `id`/`patch` for the PUT
      // itself, but `requirementId` stays part of the variables shape so
      // onSuccess below can invalidate the right queries.
      requirementId: string;
      patch: UpdateVerificationRecordInput;
    }) => updateVerification(id, patch),
    onSuccess: (_, variables) => {
      // Same invalidation set as useAddVerificationRecord above — the panel's
      // list, the detail view's verification badge, and the requirements list
      // (verificationStatus can roll up on an edit too, e.g. flipping result).
      queryClient.invalidateQueries({
        queryKey: requirementKeys.verification(variables.requirementId),
      });
      queryClient.invalidateQueries({
        queryKey: requirementKeys.detail(variables.requirementId),
      });
      queryClient.invalidateQueries({ queryKey: requirementKeys.lists() });
    },
  });
};

export const useImportRequirements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      projectId,
    }: {
      file: File;
      projectId: string;
    }): Promise<RequirementImportResult> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);

      const response = await fetch(`${API_BASE}/import`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to import requirements');
      const result = await response.json();
      return result.data as RequirementImportResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requirementKeys.lists() });
    },
  });
};

export const useGenerateTraceabilityReport = () => {
  return useMutation({
    mutationFn: async (options: TraceabilityReportOptions): Promise<TraceabilityReport> => {
      const response = await fetch(`${API_BASE}/traceability/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      });
      if (!response.ok) throw new Error('Failed to generate traceability report');
      const result = await response.json();
      return result.data as TraceabilityReport;
    },
  });
};

export const useExportCoverageMatrix = () => {
  return useMutation({
    mutationFn: async ({ projectId, format }: { projectId: string; format: 'csv' | 'excel' }) => {
      const response = await fetch(
        `${API_BASE}/coverage/export?projectId=${projectId}&format=${format}`
      );
      if (!response.ok) throw new Error('Failed to export coverage matrix');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coverage-matrix.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
};
